/* eslint-disable react/require-default-props */
import React, {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {
  Dropdown, Menu, Empty, Input, Spin, Row,
} from 'antd';
import axios from 'axios';
import * as Sentry from '@sentry/react';

import Debouncer from '../../helpers/Debouncer';
import { getIdMap } from '../../helpers/helpers';

const debouncer = new Debouncer();

function DynamicSearchWithDropdown({
  onItemClick,
  Item,
  path,
  mainItemSearchKey = 'title',
  overrideData,
  overrideSearchTerm,
  setOverrideData,
  setOverrideSearchTerm,
  onViewClicked,
  loading: propLoading,
}) {
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState([]);
  const [searchStr, setSearchStr] = useState();
  const [loading, setLoading] = useState(false);

  const dataMap = useMemo(() => getIdMap(data), [data]);

  const onChange = useCallback(async (e) => {
    if (propLoading) {
      setSearchStr();
      return;
    }
    const {
      target: {
        value,
      } = {},
    } = e;
    setSearchStr(value);
    if (setOverrideSearchTerm) setOverrideSearchTerm();
    if (setOverrideData) setOverrideData();
  }, [setOverrideSearchTerm, setOverrideData, propLoading]);

  useEffect(() => {
    const search = async () => {
      setLoading(true);
      await debouncer.debounce(async () => {
        try {
          const { data: newData = [] } = await axios.get(path, {
            params: { searchTerm: searchStr },
          });
          const sortedData = [...newData];
          sortedData.sort((a, b) => {
            if (a[mainItemSearchKey] === searchStr) return -1;
            if (b[mainItemSearchKey] === searchStr) return 1;
            return 0;
          });
          setData(sortedData);
        } catch (err) {
          Sentry.withScope(() => {
            Sentry.captureException(err);
          });
        }
        setLoading(false);
      }, 250);
    };
    setVisible(!!searchStr);
    if (searchStr && !overrideSearchTerm) {
      search();
    } else if (!overrideSearchTerm) {
      setData([]);
    }
  }, [searchStr, path, overrideSearchTerm]);

  useEffect(() => {
    if (overrideData) {
      setData(overrideData);
    }
  }, [overrideData]);

  useEffect(() => {
    if (overrideSearchTerm) {
      setSearchStr(overrideSearchTerm);
      setVisible(true);
    }
  }, [overrideSearchTerm]);

  const onClick = useCallback(async ({ key }) => {
    const shouldClose = await onItemClick(dataMap[key]);
    if (shouldClose) {
      setVisible(false);
      setSearchStr();
      if (setOverrideData) setOverrideData([]);
      if (setOverrideSearchTerm) setOverrideSearchTerm();
    }
  }, [onItemClick, dataMap, setOverrideData, setOverrideSearchTerm]);

  const onViewClick = useCallback(async (itemId) => {
    const itemData = dataMap[itemId];
    if (!itemData) return;
    const shouldClose = await onViewClicked(itemData);
    if (shouldClose) {
      setVisible(false);
      setSearchStr();
    }
  }, [onViewClicked, dataMap]);

  const menu = useMemo(() => (
    <Menu onClick={onClick}>
      {
        !loading && data.map((item) => (
          <Menu.Item key={item.id} style={{ borderBottom: '1px solid #c5c5c5' }}>
            <Item item={item} onViewClick={onViewClick} />
          </Menu.Item>
        ))
      }
      {
        data.length === 0 && !loading
        && (
        <Menu.Item disabled>
          {' '}
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Menu.Item>
        )
      }
      {
        loading && <Row justify="center" align="middle" style={{ height: 100 }}><Spin /></Row>
      }
    </Menu>
  ), [data, loading, onItemClick, onViewClick]);

  return (
    <>
      <Dropdown
        visible={visible}
        trigger={[]}
        overlay={menu}
        overlayClassName="board-search-dropdown"
      >
        <Input.Search
          type="search"
          className="searchbar"
          placeholder="Search"
          allowClear
          onChange={onChange}
          value={searchStr}
        />
      </Dropdown>
      {propLoading && (
        <Row
          justify="left"
          align="middle"
          style={{
            position: 'absolute',
            top: 2,
            left: 20,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            backgroundColor: 'transparent',
          }}
        >
          <Spin />
        </Row>
      )}
    </>
  );
}

/* eslint-disable react/forbid-prop-types */
DynamicSearchWithDropdown.propTypes = {
  onItemClick: PropTypes.func.isRequired,
  Item: PropTypes.node.isRequired,
  path: PropTypes.string.isRequired,
  mainItemSearchKey: PropTypes.string,
  overrideData: PropTypes.array,
  overrideSearchTerm: PropTypes.string,
  setOverrideData: PropTypes.func,
  setOverrideSearchTerm: PropTypes.func,
  onViewClicked: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

DynamicSearchWithDropdown.defaultProps = {
  overrideData: null,
  overrideSearchTerm: null,
  setOverrideData: null,
  setOverrideSearchTerm: null,
  loading: false,
};

export default DynamicSearchWithDropdown;
